import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CredentialRequest } from '../../types/credencial-request.type';
import { ChangeDetectorRef } from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { CredentialsInformComponent } from '../credentials/credentials-inform/credentials-inform.component';
import { AddressStatus } from '../../model/AddressStatus';
import { Subscription } from 'rxjs';
import { CpfSharedService } from 'src/app/services/cpf-shared.service';
import { ButtonModule } from 'primeng/button';
import { InputSwitchModule } from 'primeng/inputswitch';
import { DividerModule } from 'primeng/divider';
import { ToastModule } from 'primeng/toast';

@Component({
  selector: 'app-tools',
  standalone: true,
  providers: [DialogService, MessageService],
  imports: [
    FormsModule,
    ButtonModule,
    InputSwitchModule,
    DividerModule,
    ToastModule,
  ],
  template: `
    <p-toast></p-toast>
    <div class="tools-container">
      <div class="toggle-container">
        <span class="toggle-label" [class.active]="toggleApplicant">
          {{
            toggleApplicant
              ? 'Solicitante é o Beneficiário'
              : 'Solicitante Não é o Beneficiário'
          }}
        </span>
        <p-inputSwitch
          [(ngModel)]="toggleApplicant"
          (onChange)="onToggle($event)"
        >
        </p-inputSwitch>
      </div>

      <p-divider></p-divider>

      <div class="button-section">
        @if(this.valorCredentialsEnvolveds) {
          <p-button 
            label="Nova Credencial" 
            icon="pi pi-plus"
            (onClick)="openCredentalsInform()"
            styleClass="p-button-raised">
          </p-button>
        }
      </div>
    </div>
  `,
  styles: [
    `
      .tools-container {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      .toggle-container {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 1rem;
        border-radius: 6px;
        border: 1px solid #dee2e6;
        background-color: #ffffff;
      }

      .toggle-label {
        font-weight: 500;
        color: #6b7280;
        transition: color 0.3s ease;
        font-size: 0.95rem;
      }

      .toggle-label.active {
        color: #10949e;
      }

      .button-section {
        display: flex;
        gap: 0.5rem;
      }

      ::ng-deep .custom-dialog {
        border-radius: 0px !important;
      }
    `,
  ],
})
export class ToolsComponent implements OnInit, OnChanges, OnDestroy {
  valorToggle: string = 'true';
  private toggleSubscription!: Subscription;
  pessoa: string = '';

  constructor(
    private dialogService: DialogService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
    private cpfSharedService: CpfSharedService
  ) {}

  @Input() valorCredentialsEnvolveds: CredentialRequest | undefined;

  toggleApplicant: boolean = true;
  isChecked: boolean = this.toggleApplicant;

  ngOnInit(): void {
    this.isChecked = true;
    this.valorToggle = 'true';
    this.toggleApplicant = true;
    
    // Sincronizar com o serviço
    this.cpfSharedService.setToggleValue(true);
    
    this.cdr.detectChanges();

    // Corrigir a subscription
    this.toggleSubscription = this.cpfSharedService.currentToggle.subscribe((value: boolean) => {
      this.toggleApplicant = value;
      this.valorToggle = value ? 'true' : 'false';
      // console.log('Toggle atualizado via serviço:', this.valorToggle);
      
      // Atualizar a interface
      this.cdr.detectChanges();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    // Apenas atualizar estado interno quando os dados mudarem
    if (changes['valorCredentialsEnvolveds']) {
      const isSamePerson = this.valorCredentialsEnvolveds?.applicantId === this.valorCredentialsEnvolveds?.recipientId;
      this.isChecked = isSamePerson;
      
      // Sincronizar o toggle com os dados reais
      if (!isSamePerson && this.toggleApplicant) {
        this.toggleApplicant = false;
        this.valorToggle = 'false';
        this.cpfSharedService.setToggleValue(false);
        this.cdr.detectChanges();
      }
    }
    
    // Sincronizar visualização quando valorToggle mudar externamente
    if (changes['valorToggle'] && this.valorToggle) {
      this.toggleApplicant = this.valorToggle === 'true';
    }
  }

  @Output() emissorToggle = new EventEmitter<string>();

  onToggle(event: any): void {
   if (this.valorCredentialsEnvolveds) {
    this.valorToggle = event.checked ? 'true' : 'false';
    this.toggleApplicant = event.checked;
    
    // Atualizar o serviço compartilhado
    this.cpfSharedService.setToggleValue(event.checked);
    
    this.cdr.detectChanges();
    this.emissorToggle.emit(this.valorToggle);
   } else { 
    // Reverte o toggle para o estado anterior
    this.toggleApplicant = !event.checked;
    this.cdr.detectChanges();
    this.messageService.add({
      severity: 'warn',
      summary: 'Atenção',
      detail: 'Favor Primeiro Informar o Beneficiário',
      life: 3000
    });
   }
  }

  openCredentalsInform(): void {
    if (!this.valorCredentialsEnvolveds) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Favor Informar o Beneficiário',
        life: 3000
      });
      return;
    }

    if (
      this.valorAddressStatus?.isApplicantAddressOutdated ||
      this.valorAddressStatus?.isRecipientAddressOutdated
    ) {
      this.pessoa = this.valorAddressStatus.isRecipientAddressOutdated
        ? 'Beneficiário'
        : 'Solicitante';
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Favor Atualizar Endereço do ' + this.pessoa,
        life: 3000
      });
    } else {
      const dialogRef = this.dialogService.open(CredentialsInformComponent, {
        width: '500px',
        styleClass: 'custom-dialog',
        data: { ...this.valorCredentialsEnvolveds },
      });

      if (dialogRef) {
        dialogRef
          .onClose
          .subscribe((result: CredentialRequest | undefined) => {
            if (result) {
              this.valorCredentialsEnvolveds = result;
            }
          });
      }
    }
  }

  @Input() valorAddressStatus: AddressStatus | undefined;

  ngOnDestroy() {
    if (this.toggleSubscription) {
      this.toggleSubscription.unsubscribe();
    }
  }
}